/*
Business logic functions for the ns-shipping component
*/

/**
 * Parses a string representing a range of positive integers and returns an ordered list of all integers within that range.
 * For example, the string "1,23,6-9,20" becomes [1,6,7,8,9,20,23].
 * @param {string} str - Integer range string to parse
 * @returns {number[]} Ordered list of integers within the range
 * @throws {SyntaxError} if the string to parse does not represent a valid range
 */
export function parseIntRange(str = "") {
  const ints = str
    .replace(/\s+/g, "")
    .split(",")
    .map(s => {
      if (!s) {
        return [];
      } else if (/^\d+$/.test(s)) {
        return [parseInt(s)];
      } else if (/^\d+-\d+$/.test(s)) {
        const [a, b] = s.split("-").map(x => parseInt(x));
        if (b < a)
          throw new SyntaxError(
            `Invalid range token ${s} at position ${str.indexOf(s)}`
          );
        return Array.from(Array(b - a + 1), (e, i) => i + a);
      } else
        throw new SyntaxError(
          `Unexpected token ${s} at position ${str.indexOf(s)}`
        );
    })
    .reduce((acc, arr) => acc.concat(arr), [])
    .sort((a, b) => a - b);
  return [...new Set(ints)];
}

/**
 * Creates a list of valid ADR processing date options.
 * @param {{
 * now: Date,
 * nextDeliveryDate: Date,
 * disabledDays: number[],
 * blackoutPeriod: number,
 * shippingRestrictedDays?: number[]
 * }} options - List creation options and restrictions
 * @param {Date} options.now - Current date.
 * @param {Date} options.nextDeliveryDate - Next scheduled delivery date.
 * @param {number[]} options.disabledDays - Days of the month that are disallowed for ADR processing.
 * @param {number} options.blackoutPeriod - Days in advance of the current date that the ADR processing date cannot be set to.
 * @param {number[]} [options.shippingRestrictedDays] - (optional) Days of the month that are disallowed by the selected shipping method.
 * @returns {Date[]} List of processing dates
 */
export function createProcessingDateList(options) {
  return (
    [...Array(60).keys()]
      // Create list of potential processing dates advancing from NOW in one-day increments
      .map(n => {
        const date = new Date(options.nextDeliveryDate);
        date.setFullYear(
          options.now.getFullYear(),
          options.now.getMonth(),
          options.now.getDate() + n + 1
        );
        return date;
      })
      // Remove dates that fall on market's ADR processing disabled days.
      // Remove dates not allowed by the current shipping method, if applicable.
      .filter(
        date =>
          !options.disabledDays.includes(date.getDate()) ||
          (options.shippingRestrictedDays &&
            options.shippingRestrictedDays.includes(date.getDate()))
      )
      // Remove dates that fall too close to NOW, as defined by the market's blackout period length
      .filter(
        date =>
          date.getTime() - options.now.getTime() >
          options.blackoutPeriod * (24 * 60 * 60 * 1000)
      )
      /*
      Sort list and remove duplicate day-of-month entries:
      Array is sorted first by day-of-month and then by timestamp. This places dates that fall on the same 
      day-of-month adjacent to each other, with the earlier date placed lower in the array.
      Array is then filtered to remove all entries whose day-of-month matches that of their lower neighbor.
      */
      .sort((a, b) =>
        a.getDate() === b.getDate()
          ? a.getTime() - b.getTime()
          : a.getDate() - b.getDate()
      )
      .filter((date, idx, arr) =>
        idx === 0 ? true : date.getDate() !== arr[idx - 1].getDate()
      )
  );
}

/**
 *
 * @param {{
 * length: number,
 * now: Date,
 * nextProcessDate: Date,
 * disabledDays: number[],
 * blackoutPeriod: number
 * allowedDays: number[]
 * }} options
 * @param {number} options.length - Number of days out from NOW to evaluate
 * dates.
 * @param {Date} options.now - Current date.
 * @param {Date} options.nextDeliveryDate - Next scheduled delivery date.
 * @param {number[]} options.disabledDays - Days of the month that are
 * disallowed for ADR processing.
 * @param {number} options.blackoutPeriod - Days in advance of the current date
 * that the ADR processing date cannot be set to.
 * @param {number[]} [options.allowedDays] - (optional) Days of the month that
 * are disallowed by the selected shipping method.
 * @returns {Date[]}
 */
export function createDateList(options) {
  return (
    [...Array(options.length).keys()]
      .map(n => {
        const date = new Date(options.nextProcessDate);
        date.setFullYear(
          options.now.getFullYear(),
          options.now.getMonth(),
          options.now.getDate() + n + 1
        );
        return date;
      })
      /* 
      Remove dates that are not allowed by market's shipping method or that 
      fall on market's ADR processing disabled days.
      */
      .filter(date =>
        (options.allowedDays || []).length > 0
          ? options.allowedDays.includes(date.getDate())
          : !(options.disabledDays || []).includes(date.getDate())
      )
      /* 
      Remove dates that fall too close to NOW as defined by the market's 
      blackout period.
      */
      .filter(
        date =>
          date.getTime() - options.now.getTime() >
          options.blackoutPeriod * (24 * 60 * 1000)
      )
  );
}
